import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Truck, RefreshCw, ShieldCheck, Star, Check, ChevronRight, Heart } from "lucide-react";
import { toast } from "sonner";

import ProductCard from "./components/ProductCard";
import SafeImage from "../../components/SafeImage";
import { fetchProduct, fetchProducts } from "../../lib/api";
import { useTemplate } from "../../context/TemplateContext";
import { useCart } from "../../context/CartContext";

/**
 * SRC-styled product detail page. Two-column layout on desktop:
 *   Left:  primary image + thumbnail rail (up to 4 variants)
 *   Right: category crumb, name, price, star rating, description,
 *          size picker (chip grid), color picker (circle swatches),
 *          Add to cart button, feature list, related products (4-col).
 */
export default function ProductDetail() {
  const { slug: productSlug } = useParams();
  const { config: cfg, basePath, slug: siteSlug } = useTemplate();
  const accent = cfg.brand?.accentColor || "#E60000";
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [imgIdx, setImgIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [wishlisted, setWishlisted] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    setImgIdx(0);
    fetchProduct(productSlug, { slug: siteSlug, fallback: cfg.demoProducts })
      .then((p) => {
        setProduct(p);
        setSize(p.sizes?.[0] || "");
        setColor((p.colors?.[0]?.name || p.colors?.[0] || ""));
        fetchProducts({ slug: siteSlug, category: p.category, limit: 8, fallback: cfg.demoProducts })
          .then((list) => setRelated(list.filter((x) => (x.slug || x.id) !== (p.slug || p.id)).slice(0, 4)));
      })
      .finally(() => setLoading(false));
  }, [productSlug, siteSlug, cfg.demoProducts]);

  if (loading || !product) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-32">
        <div className="grid lg:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-[3/4] bg-neutral-100" />
          <div className="space-y-4">
            <div className="h-8 bg-neutral-100 w-2/3" />
            <div className="h-4 bg-neutral-100 w-1/3" />
            <div className="h-20 bg-neutral-100" />
          </div>
        </div>
      </div>
    );
  }

  const onSale = !!product.compare_at_price && product.compare_at_price > product.price;
  const discount = onSale ? Math.round(100 - (product.price / product.compare_at_price) * 100) : 0;
  const images = product.images || [product.image_url].filter(Boolean);
  const colorList = (product.colors || []).map((c) => (typeof c === "string" ? { name: c, hex: null } : c));
  const rating = typeof product.rating === "number" ? product.rating : null;

  const handleAdd = () => {
    if (product.sizes?.length && !size) { toast.error("Pick a size"); return; }
    if (colorList.length && !color) { toast.error("Pick a colour"); return; }
    addItem({
      product_id: product.id || product.slug,
      slug: product.slug,
      name: product.name,
      price: Number(product.price),
      image: images[0],
      variant: { ...(size ? { size } : {}), ...(color ? { color } : {}) },
      quantity: 1,
    });
  };

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      {/* Breadcrumb */}
      <div className="text-xs text-neutral-500 mb-8 flex items-center gap-2 uppercase tracking-[0.2em]">
        <Link to={`${basePath}/`} className="link-underline pb-0.5">Home</Link>
        <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
        <Link to={`${basePath}/shop`} className="link-underline pb-0.5">Shop</Link>
        <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
        <span className="text-black">{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Left: gallery */}
        <div>
          <div className="relative aspect-[3/4] bg-neutral-100 overflow-hidden">
            <SafeImage
              src={images[imgIdx]}
              alt={product.name}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover" }}
              testId="product-hero-image"
              fallbackLabel={product.name}
            />
            {onSale && (
              <span className="absolute top-4 left-4 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1" style={{ background: accent }}>
                -{discount}%
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {images.slice(0, 4).map((im, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-[3/4] bg-neutral-100 overflow-hidden border ${imgIdx === i ? "border-black" : "border-neutral-200"}`}
                >
                  <SafeImage src={im} alt="" className="w-full h-full" style={{ objectFit: "cover" }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: info */}
        <div>
          {product.category && (
            <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">— {product.category}</p>
          )}
          <h1 data-testid="product-name" className="font-display text-4xl sm:text-5xl mt-3 leading-none">{product.name}</h1>

          <div className="mt-5 flex items-baseline gap-3">
            <p data-testid="product-price" className="font-heading text-2xl">${Number(product.price).toFixed(2)}</p>
            {onSale && <p className="text-sm text-neutral-400 line-through">${Number(product.compare_at_price).toFixed(2)}</p>}
          </div>

          {rating !== null && (
            <div className="mt-3 flex items-center gap-2 text-sm text-neutral-500">
              <Star className="w-4 h-4 fill-current text-amber-500" strokeWidth={1.5} />
              {rating.toFixed(1)} {product.review_count ? `(${product.review_count} reviews)` : ""}
            </div>
          )}

          {product.description && (
            <p className="mt-6 text-sm text-neutral-700 leading-relaxed">{product.description}</p>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-[0.2em]">Size</p>
                <a href="#size-guide" className="text-xs uppercase tracking-[0.2em] link-underline pb-0.5">Size guide</a>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                      size === s ? "bg-black text-white border-black" : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {colorList.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] mb-3">Colour: {color}</p>
              <div className="flex items-center gap-2">
                {colorList.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setColor(c.name)}
                    data-testid={`color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={c.name}
                    className={`w-8 h-8 rounded-full border-2 transition-colors ${
                      color === c.name ? "border-black" : "border-neutral-300 hover:border-black"
                    }`}
                    style={{ background: c.hex || "#e5e5e5" }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-2">
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
              className="flex-1 bg-black text-white hover:bg-neutral-800 px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Add to cart · ${Number(product.price).toFixed(2)}
            </button>
            <button
              onClick={() => setWishlisted((v) => !v)}
              data-testid="wishlist-btn"
              className="h-14 w-14 grid place-items-center border border-neutral-300 hover:border-black transition-colors"
              aria-label="Save to wishlist"
            >
              <Heart className={`w-5 h-5 ${wishlisted ? "fill-current" : ""}`} strokeWidth={1.5} style={{ color: wishlisted ? accent : "inherit" }} />
            </button>
          </div>

          <ul className="mt-8 border-t border-neutral-200 pt-8 grid sm:grid-cols-2 gap-4 text-sm">
            {[
              { icon: Truck, text: "Free shipping over $100" },
              { icon: RefreshCw, text: "30-day free returns" },
              { icon: ShieldCheck, text: "Secure checkout" },
              { icon: Check, text: "Ships in 1–3 business days" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2 text-neutral-700">
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {text}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-display text-3xl sm:text-4xl mb-8">You might also like.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
            {related.map((p) => (
              <ProductCard key={p.id || p.slug} product={p} basePath={basePath} accent={accent} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
